import { del, get, keys, set } from 'idb-keyval'

import {
  ProjectRepository,
  type ProjectKeyValueStore,
  type ProjectLoadResult,
} from '../v2/persistence/ProjectRepository.js'

export const DEFAULT_PROJECT_ID = 'default'
export const LEGACY_PROJECT_STORAGE_KEY = 'nastaliqart_settings'

const DEFAULT_PROJECT_STORAGE_KEY = `project:${DEFAULT_PROJECT_ID}`

export interface BrowserProjectStoreDependencies {
  readonly get: (key: IDBValidKey) => Promise<unknown>
  readonly set: (key: IDBValidKey, value: unknown) => Promise<void>
  readonly del: (key: IDBValidKey) => Promise<void>
  readonly keys: () => Promise<IDBValidKey[]>
  readonly readLegacySettings: () => unknown
  readonly deleteLegacySettings: () => void
}

function readBrowserLegacySettings(): unknown {
  try {
    const raw = globalThis.localStorage?.getItem(LEGACY_PROJECT_STORAGE_KEY)
    return raw === null || raw === undefined ? undefined : JSON.parse(raw)
  } catch {
    return undefined
  }
}

function deleteBrowserLegacySettings(): void {
  globalThis.localStorage?.removeItem(LEGACY_PROJECT_STORAGE_KEY)
}

/**
 * IndexedDB project storage with a read-only V1 localStorage fallback. The
 * fallback is consulted only for the canonical project and only when no V2
 * record exists, so stale legacy settings can never beat a V2 envelope.
 */
export function createBrowserProjectStore(
  dependencies: BrowserProjectStoreDependencies = {
    get,
    set,
    del,
    keys,
    readLegacySettings: readBrowserLegacySettings,
    deleteLegacySettings: deleteBrowserLegacySettings,
  },
): ProjectKeyValueStore {
  return {
    async get(key) {
      const current = await dependencies.get(key)
      if (current !== undefined || key !== DEFAULT_PROJECT_STORAGE_KEY) return current
      try {
        return dependencies.readLegacySettings()
      } catch {
        return undefined
      }
    },
    set: (key, value) => dependencies.set(key, value),
    async delete(key) {
      // Removing the canonical project must remove the V1 fallback first, or a
      // later load would resurrect and remigrate the supposedly deleted data.
      if (key === DEFAULT_PROJECT_STORAGE_KEY) dependencies.deleteLegacySettings()
      await dependencies.del(key)
    },
    async keys() {
      const values = await dependencies.keys()
      return values.filter((key): key is string => typeof key === 'string')
    },
  }
}

export interface DefaultProjectRepository {
  load(id: string): Promise<ProjectLoadResult>
}

export interface DefaultProjectLoader {
  loadOnce(): Promise<ProjectLoadResult>
}

/** Shares one hydration across React StrictMode remounts and retries failures. */
export function createDefaultProjectLoader(repository: DefaultProjectRepository): DefaultProjectLoader {
  let cached: Promise<ProjectLoadResult> | null = null
  return {
    loadOnce() {
      if (cached !== null) return cached
      const loading = repository.load(DEFAULT_PROJECT_ID)
      cached = loading
      void loading.catch(() => {
        if (cached === loading) cached = null
      })
      return loading
    },
  }
}

const projectRepository = new ProjectRepository(createBrowserProjectStore())
const defaultProjectLoader = createDefaultProjectLoader(projectRepository)

export function loadDefaultProjectOnce(): Promise<ProjectLoadResult> {
  return defaultProjectLoader.loadOnce()
}

export function saveDefaultProject(document: unknown): Promise<void> {
  return projectRepository.save(DEFAULT_PROJECT_ID, document)
}

export function deleteDefaultProject(): Promise<void> {
  return projectRepository.delete(DEFAULT_PROJECT_ID)
}
